import React, { useState, useEffect } from 'react';
import { Clock, Flame, Sparkles, TrendingUp, Star, Baby } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { productsAPI, categoriesAPI } from '@/lib/api';
import { useAppData } from '@/context/AppDataContext';

import { WebsiteJsonLd, OrganizationJsonLd, ProductListJsonLd } from '@/components/seo/JsonLd';
import HeroBanner from '@/components/home/HeroBanner';
import CategoryButtons from '@/components/home/CategoryButtons';
import ProductCarousel from '@/components/home/ProductCarousel';
import FlashOffersCarousel from '@/components/home/FlashOffersCarousel';
import PartnersSection from '@/components/home/PartnersSection';
import LeadCaptureSection from '@/components/home/LeadCaptureSection';
import TestimonialsSection from '@/components/home/TestimonialsSection';
import FAQSection from '@/components/home/FAQSection';

export const Home = () => {
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [offerProducts, setOfferProducts] = useState([]);
  const [viralProducts, setViralProducts] = useState([]);
  const [kidsProducts, setKidsProducts] = useState([]);
  
  // Usa o contexto global para settings em vez de fazer fetch próprio
  const { settings: siteSettings } = useAppData();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Primeiro busca categorias para pegar o ID da categoria "Crianças"
        const categoriesRes = await categoriesAPI.getAll();
        const categories = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        const kidsCategory = categories.find(c => c.slug === 'criancas');
        
        // Busca produtos
        const requests = [
          productsAPI.getAll({ limit: 50 }),
          productsAPI.getAll({ is_featured: true, limit: 12 }),
          productsAPI.getAll({ is_offer: true, limit: 10 }),
          productsAPI.getAll({ is_viral: true, limit: 10 }),
        ];
        
        // Adiciona busca de produtos infantis se a categoria existir
        if (kidsCategory) {
          requests.push(productsAPI.getAll({ category_id: kidsCategory.id, limit: 12 }));
        }
        
        const responses = await Promise.all(requests);
        const [productsRes, featuredRes, offersRes, viralsRes, kidsRes] = responses;

        const allProds = Array.isArray(productsRes.data) ? productsRes.data : [];
        const featured = Array.isArray(featuredRes.data) ? featuredRes.data : [];
        const offers = Array.isArray(offersRes.data) ? offersRes.data : [];
        const virals = Array.isArray(viralsRes.data) ? viralsRes.data : [];
        const kids = kidsRes && Array.isArray(kidsRes.data) ? kidsRes.data : [];
        
        // IDs dos produtos que já estão nas seções de destaque
        const highlightedIds = new Set([
          ...featured.map(p => p.id),
          ...offers.map(p => p.id),
          ...virals.map(p => p.id),
          ...kids.map(p => p.id)
        ]);
        
        // Achadinhos do Dia: produtos que NÃO estão em nenhuma seção destacada
        const achadinhos = allProds.filter(p => !highlightedIds.has(p.id)).slice(0, 12);

        setAllProducts(achadinhos);
        setFeaturedProducts(featured);
        setOfferProducts(offers);
        setViralProducts(virals);
        setKidsProducts(kids);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <Layout>
      <WebsiteJsonLd siteSettings={siteSettings} />
      <OrganizationJsonLd />
      <ProductListJsonLd products={allProducts} />
      <HeroBanner siteSettings={siteSettings} />

      <CategoryButtons />

      {/* Mobile: Promo Banner */}
      <div
        className="md:hidden bg-[#FF4500] text-white py-2.5 px-4 flex items-center justify-center gap-2"
        data-testid="mobile-promo-banner"
      >
        <Clock className="w-4 h-4" />
        <span className="font-bold text-sm">PROMOCOES RELAMPAGO!!</span>
        <Flame className="w-4 h-4" />
      </div>

      {/* OFERTAS RELÂMPAGO - DESTAQUE PRINCIPAL */}
      <div id="flash">
        <FlashOffersCarousel products={offerProducts} loading={loading} />
      </div>

      {/* Produtos em Destaque — Carousel */}
      {(loading || featuredProducts.length > 0) && (
        <section className="py-8 md:py-12 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <ProductCarousel
              products={featuredProducts}
              loading={loading}
              title="Produtos em Destaque"
              icon={Star}
              viewAllLink="/ofertas"
            />
          </div>
        </section>
      )}

      {/* Achadinhos do Dia — Carousel */}
      <section id="produtos" className="py-8 md:py-12 bg-[#f8fafc]">
        <div className="container mx-auto px-4 max-w-7xl">
          <ProductCarousel
            products={allProducts.slice(0, 12)}
            loading={loading}
            title="Achadinhos do Dia"
            icon={Sparkles}
            viewAllLink="/ofertas"
          />
        </div>
      </section>

      {/* Produtos Virais — Carousel */}
      <section id="virais" className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <ProductCarousel
            products={viralProducts}
            loading={loading}
            title="Produtos Virais"
            icon={TrendingUp}
            viewAllLink="/ofertas"
          />
        </div>
      </section>

      {/* Produtos Infantis — Carousel */}
      {(loading || kidsProducts.length > 0) && (
        <section id="criancas" className="py-8 md:py-12 bg-[#FFF0F5]">
          <div className="container mx-auto px-4 max-w-7xl">
            <ProductCarousel
              products={kidsProducts}
              loading={loading}
              title="Produtos Infantis"
              icon={Baby}
              viewAllLink="/categoria/criancas"
            />
          </div>
        </section>
      )}

      <PartnersSection />
      <LeadCaptureSection />
      <TestimonialsSection />
      <FAQSection />
    </Layout>
  );
};

export default Home;
